import React, { useMemo, useState } from "react";
import { ActionIcon, Group, Select, Tooltip } from "@mantine/core";
import { IconChevronLeft as ChevronLeftIcon, IconChevronRight } from '@tabler/icons-react';
import { useObjectStore } from "../zustand/objects";
import {
  CHROME_TOOLBAR_GROUP_HEIGHT,
  CHROME_TOOLBAR_ICON_GLYPH,
  chromeToolbarGroupStyle,
  chromeToolbarIconButtonProps,
} from "../utils/editorChromeTheme";
import { CONTROL_RADIUS } from "../utils/uiChromeTokens";

/** Upper cap for scene select input width — dropdown matches this, not the live narrower width. */
const SCENE_SELECT_INPUT_MAX_WIDTH = 118;

const EditorSceneNavigator = ({ iconColour }) => {
  const { currentObjectArtboard, updateArtboardAndPreviewCamera, artboards, storyboardFields } = useObjectStore();

  const sceneKeys = useMemo(() => {
    if (!artboards) return [];
    return Object.keys(artboards)
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
  }, [artboards]);

  const current = Number(currentObjectArtboard) || (sceneKeys[0] ?? 1);
  const minScene = sceneKeys.length ? sceneKeys[0] : 1;
  const maxScene = sceneKeys.length ? sceneKeys[sceneKeys.length - 1] : 1;

  const backDisabled = current <= minScene;
  const forwardDisabled = current >= maxScene;

  const [hoverBack, setHoverBack] = useState(false);
  const [hoverForward, setHoverForward] = useState(false);

  const currentLabel =
    storyboardFields?.[String(current)]?.name ||
    storyboardFields?.[current]?.name ||
    String(current);

  const sceneSelectInputWidth = useMemo(() => {
    const len = currentLabel.length;
    const charApprox = Math.ceil(len * 9.5);
    return Math.min(SCENE_SELECT_INPUT_MAX_WIDTH, Math.max(44, charApprox + 34));
  }, [currentLabel]);

  const chevronIconSlot = {
    width: CHROME_TOOLBAR_GROUP_HEIGHT,
    flex: `0 0 ${CHROME_TOOLBAR_GROUP_HEIGHT}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "stretch",
    minHeight: CHROME_TOOLBAR_GROUP_HEIGHT,
  };

  const groupStyle = chromeToolbarGroupStyle(iconColour);

  return (
    <Group
      gap={0}
      align="stretch"
      wrap="nowrap"
      style={{
        ...groupStyle,
        padding: 0,
      }}
    >
      <Tooltip label="Previous scene">
        <ActionIcon
          radius={0}
          variant="transparent"
          disabled={backDisabled}
          onClick={() => updateArtboardAndPreviewCamera(String(current - 1))}
          onPointerOver={() => setHoverBack(true)}
          onPointerLeave={() => setHoverBack(false)}
          aria-label="Previous scene"
          styles={{
            root: {
              ...chevronIconSlot,
              borderTopLeftRadius: CONTROL_RADIUS,
              borderBottomLeftRadius: CONTROL_RADIUS,
              ...chromeToolbarIconButtonProps(hoverBack, iconColour, backDisabled, true).style,
            },
          }}
        >
          <ChevronLeftIcon size={CHROME_TOOLBAR_ICON_GLYPH} />
        </ActionIcon>
      </Tooltip>

      <div
        style={{
          height: CHROME_TOOLBAR_GROUP_HEIGHT,
          borderLeft: `1px solid ${iconColour?.border || "rgba(229,231,235,0.8)"}`,
          borderRight: `1px solid ${iconColour?.border || "rgba(229,231,235,0.8)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: 6,
          paddingRight: 6,
          flexShrink: 0,
        }}
      >
        <Select
          size="xs"
          value={String(current)}
          onChange={(value) => updateArtboardAndPreviewCamera(String(value))}
          data={sceneKeys.map((k) => ({
            value: String(k),
            label:
              storyboardFields?.[String(k)]?.name ||
              storyboardFields?.[k]?.name ||
              String(k),
          }))}
          styles={{
            root: { width: sceneSelectInputWidth },
            wrapper: { width: sceneSelectInputWidth },
            input: {
              fontSize: 13,
              fontWeight: 500,
              border: "none",
              background: "transparent",
              width: sceneSelectInputWidth,
              minHeight: CHROME_TOOLBAR_GROUP_HEIGHT - 4,
              height: CHROME_TOOLBAR_GROUP_HEIGHT - 4,
              color: iconColour?.normal || "#4a5565",
              textAlign: "center",
              paddingLeft: 4,
              paddingRight: 22,
            },
            section: { color: iconColour?.normal || "#98a2b3", width: 20 },
            item: {
              fontSize: 13,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            dropdown: {
              borderRadius: CONTROL_RADIUS,
              width: `${SCENE_SELECT_INPUT_MAX_WIDTH}px`,
              minWidth: `${SCENE_SELECT_INPUT_MAX_WIDTH}px`,
              maxWidth: `${SCENE_SELECT_INPUT_MAX_WIDTH}px`,
            },
          }}
        />
      </div>

      <Tooltip label="Next scene">
        <ActionIcon
          radius={0}
          variant="transparent"
          disabled={forwardDisabled}
          onClick={() => updateArtboardAndPreviewCamera(String(current + 1))}
          onPointerOver={() => setHoverForward(true)}
          onPointerLeave={() => setHoverForward(false)}
          aria-label="Next scene"
          styles={{
            root: {
              ...chevronIconSlot,
              borderTopRightRadius: CONTROL_RADIUS,
              borderBottomRightRadius: CONTROL_RADIUS,
              ...chromeToolbarIconButtonProps(hoverForward, iconColour, forwardDisabled, true).style,
            },
          }}
        >
          <IconChevronRight size={CHROME_TOOLBAR_ICON_GLYPH} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

export default EditorSceneNavigator;
